import { Button, Col, DatePicker, Divider, Form, Input, notification, Popconfirm, Row, Table, Tooltip } from 'antd'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import { Suspense, useEffect, useRef, useState } from 'react'
import {
  Icon,
  IconContainer,
  InfoModal,
} from '../../../../components'
import { handleBackendError } from '../../../../utils'
import { deletePaymentMethod, getBankData, getPaymentMethods, updatePaymentMethod } from '../../api'

const BankDetails = ({ form, handleSuccess, isUpdate, selectedStore }) => {
  const emailInputRef = useRef()
  const legalEntityInputRef = useRef()

  const [isLoading, setIsLoading] = useState(false)
  const [selectedFromDate, setSelectedFromDate] = useState(undefined)
  const [selectedToDate, setSelectedToDate] = useState(undefined)
  const [editRow, setEditRow] = useState(null)

  const [paymentMethods, setPaymentMethods] = useState([])

  useEffect(() => {
    if (selectedStore)
      fetchPaymentMethods()
  }, [selectedStore])

  const fetchPaymentMethods = () => {
    setIsLoading(true)
    setEditRow(null)
    getPaymentMethods(selectedStore.id)
      .then(({ data }) => {
        setPaymentMethods(data.data)
      })
      .catch((error) => {
        notification.error({
          description: handleBackendError(error, 'Fetching payment method list failed!'),
          message: 'Error',
        })
      })
      .finally(() => setIsLoading(false))
  }

  const removePaymentMethod = (recordId) => {
    deletePaymentMethod(recordId)
      .then(() => {
        fetchPaymentMethods()

        handleSuccess({ description: 'Payment method removed' }, false)
      })
      .catch((error) => notification.error({
        description: handleBackendError(error, 'Payment method removal failed!'),
        message: 'Error',
      }))
  }

  const updatePaymentMethodRow = () => {
    const isFieldsTouched = form.isFieldsTouched()

    if (!isFieldsTouched) {
      setEditRow(null)
      return
    }

    setIsLoading(true)

    const emailInput = emailInputRef.current.input.value
    const legalEntityInput = legalEntityInputRef.current.input.value

    const fields = {
      end_date: selectedToDate ? selectedToDate.format('DD/MM/YYYY') : null,
      legal_entity: legalEntityInput || editRow?.legal_entity || '',
      reporting_email: emailInput || editRow?.reporting_email || '',
      start_date: selectedFromDate ? selectedFromDate.format('DD/MM/YYYY') : null,
    }

    updatePaymentMethod(editRow.id, { ...fields })
      .then(() => {
        setEditRow(null)
        fetchPaymentMethods()

        handleSuccess({ description: 'Payment method updated' }, false)
      })
      .catch((error) => {
        notification.error({
          description: handleBackendError(error, 'Error'),
          message: 'Payment method update failed!',
        })
      })
      .finally(() => {
        fetchPaymentMethods()
        setIsLoading(false)
      })

  }

  const getMinFromDate = (start_date = null) => {
    if (!editRow) return undefined

    return start_date
      ? dayjs(start_date)?.add(1, 'day')
      : dayjs()
  }

  const getMaxFromDate = () => {
    if (!editRow) return undefined

    return selectedToDate
      ? dayjs(selectedToDate)?.add(-1, 'day')
      : undefined
  }

  const getMinToDate = () => {
    if (!editRow) return undefined

    return selectedFromDate
      ? dayjs(selectedFromDate)?.add(1, 'day')
      : dayjs()
  }

  const tableColumns = [
    {
      className: 'paymentAccountNumber',
      dataIndex: 'account_number',
      key: 'account_number',
      title: 'Wise ID',
    },
    {
      className: 'paymentReportingEmail',
      dataIndex: 'reporting_email',
      key: 'reporting_email',
      render: (text, record) => (
        (editRow?.id === record.id)
          ? (
            <Form.Item
              name="email"
              rules={[
                { message: '', required: true, type: 'email' },
              ]}
            >
              <Input defaultValue={text} ref={emailInputRef} />
            </Form.Item>
          )
          : <p>{record.reporting_email}</p>
      ),
      title: 'Reporting email',
    },
    {
      className: 'paymentLegalEntity',
      dataIndex: 'legal_entity',
      key: 'legal_entity',
      render: (text, record) => (
        (editRow?.id === record.id)
          ? (
            <Form.Item
              name="legal_entity"
              rules={[
                { message: '', required: true },
              ]}
            >
              <Input defaultValue={text} ref={legalEntityInputRef} />
            </Form.Item>
          )
          : <p>{record.legal_entity}</p>
      ),
      title: 'Legal entity',
    },
    {
      dataIndex: 'start_date',
      key: 'start_date',
      render: (_, record) => (
        (editRow?.id === record.id)
          ? (
            <Form.Item name="start_date">
              <DatePicker allowClear={false} defaultValue={record.start_date ? dayjs(record.start_date) : undefined} format="DD.MM.YYYY" maxDate={getMaxFromDate()} minDate={getMinFromDate(record.start_date)} onChange={(date) => setSelectedFromDate(date)} />
            </Form.Item>
          )
          : <p>{record.start_date ? dayjs(record.start_date).format('DD.MM.YYYY') : ''}</p>
      ),
      title: 'From',
    },
    {
      dataIndex: 'end_date',
      key: 'end_date',
      render: (_, record) => (
        (editRow?.id === record.id)
          ? (
            <Form.Item name="end_date">
              <DatePicker defaultValue={record.end_date ? dayjs(record.end_date) : undefined} format="DD.MM.YYYY" minDate={getMinToDate(record.start_date)} onChange={(date) => setSelectedToDate(date)} />
            </Form.Item>
          )
          : <p>{record.end_date ? dayjs(record.end_date).format('DD.MM.YYYY') : ''}</p>
      ),
      title: 'To',
    },
    {
      className: 'userActionSelenium',
      key: 'action',
      render: (_, record) => (
        (editRow?.id === record.id)
          ? <Button onClick={updatePaymentMethodRow} type="primary">Update</Button>
          : (
            <IconContainer>
              {record.editable &&
                <Tooltip placement="bottom" title="Edit reporting email or start date">
                  <Icon
                    data-testid="addBankDetails"
                    name="addBankDetailsIconSelenium"
                    onClick={() => {
                      setSelectedFromDate(record.start_date ? dayjs(record.start_date) : null)
                      setSelectedToDate(record.end_date ? dayjs(record.end_date) : null)

                      form.setFieldsValue({
                        email: record.reporting_email,
                        end_date: record.end_date ? dayjs(record.end_date) : null,
                        legal_entity: record.legal_entity,
                        start_date: record.start_date ? dayjs(record.start_date) : null,
                      })

                      setEditRow(record)
                    }}
                    type="edit"
                  />
                </Tooltip>
              }

              {record.deletable &&
                <Popconfirm
                  cancelText="No"
                  className="action-list-button"
                  okText="Yes"
                  onConfirm={() => removePaymentMethod(record.id)}
                  title="Are you sure you want to delete this payment method?"
                >
                  <Tooltip placement="bottom" title="Delete payment method">
                    <Icon
                      data-testid="deleteRow"
                      name="addBankDetailsIconSelenium"
                      type="delete"
                    />
                  </Tooltip>
                </Popconfirm>
              }
            </IconContainer>
          )
      ),
      width: 30,
    },
  ]

  return (
    <Row style={{ margin: 15 }}>
      <Col span={24}>
        {isUpdate && (
          <Form.Item label="Balance" name="balance">
            <Input disabled />
          </Form.Item>
        )}
      </Col>

      <Divider>Payment Methods</Divider>

      <Col span={24}>
        <Table
          className="paymentsPageTableSelenium"
          columns={tableColumns}
          data-testid="paymentsTable"
          dataSource={paymentMethods}
          expandable={{
            expandedRowClassName: 'default-payment-row',
            expandedRowRender: () => (
              <Suspense fallback={<div>Loading...</div>}>
                <InfoModal
                  apiRequest={getBankData}
                  errorMessage={''}
                  hideModal={() => () => { }}
                  isEmbedded
                  selectedId={selectedStore.id}
                  title="Bank data details info"
                />
              </Suspense>
            ),
            rowExpandable: record => record.default,
          }}
          loading={isLoading}
          rowClassName={(record) => record.default ? 'default-payment-row' : ''}
          rowKey={({ id }) => id}
          size="small"
        />
      </Col>
    </Row>
  )
}

BankDetails.propTypes = {
  form: PropTypes.object.isRequired,
  handleSuccess: PropTypes.func,
  isUpdate: PropTypes.bool,
  selectedStore: PropTypes.object,
}

export default BankDetails
